import React, { useEffect, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { PageHero } from '../components/PageHero';
import { motion } from 'framer-motion';
import { Calendar, MapPin, ExternalLink, Navigation } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EventsPage = () => {
  const { t, language } = useLanguage();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteImages, setSiteImages] = useState({ page_heroes: {} });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, imagesRes] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/site-images`)
        ]);
        const sortedEvents = eventsRes.data.sort((a, b) => 
          new Date(a.start_date) - new Date(b.start_date)
        );
        setEvents(sortedEvents);
        setSiteImages(imagesRes.data);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getDescription = (event) => {
    const key = `description_${language}`;
    return event[key] || event.description_it;
  };

  const formatDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    const locale = language === 'de' ? 'de-DE' : language === 'fr' ? 'fr-FR' : language === 'en' ? 'en-GB' : 'it-IT';
    
    const startStr = startDate.toLocaleDateString(locale, { day: 'numeric', month: 'long' });
    const endStr = endDate.toLocaleDateString(locale, options);
    
    return `${startStr} - ${endStr}`;
  };

  const getGoogleMapsUrl = (event) => {
    if (event.lat && event.lng) {
      return `https://www.google.com/maps?q=${event.lat},${event.lng}`;
    }
    return `https://www.google.com/maps/search/${encodeURIComponent(event.address || event.location)}`;
  };

  return (
    <div data-testid="events-page" className="pt-20">
      {/* Hero */}
      <PageHero
        title={t('events.title')}
        subtitle={t('events.subtitle')}
        backgroundUrl={siteImages.page_heroes?.events}
        darkMode={true}
      />

      {/* Events List */}
      <section className="py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          {loading ? (
            <div className="space-y-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-[#F5F5F7] animate-pulse h-96 rounded-lg" />
              ))}
            </div>
          ) : (
            <div className="space-y-12">
              {events.map((event, index) => (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`event-item-${event.id}`}
                  className="bg-white border border-[#E5E5EA] overflow-hidden"
                >
                  <div className="grid grid-cols-1 lg:grid-cols-2">
                    {/* Event Image */}
                    <div className="aspect-video lg:aspect-auto lg:h-full overflow-hidden">
                      {event.image ? (
                        <img
                          src={event.image}
                          alt={event.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-[#F5F5F7] flex items-center justify-center">
                          <Calendar className="w-16 h-16 text-[#E5E5EA]" />
                        </div>
                      )}
                    </div>
                    
                    {/* Event Content */}
                    <div className="p-8 lg:p-10">
                      {/* Date Badge */}
                      <div className="inline-flex items-center gap-2 bg-[#FF3B30] text-white px-4 py-2 mb-6">
                        <Calendar className="w-4 h-4" />
                        <span className="font-medium text-sm">
                          {formatDateRange(event.start_date, event.end_date)}
                        </span>
                      </div>

                      <h2 className="text-3xl font-bold text-[#0B1221] mb-4">
                        {event.name}
                      </h2>
                      
                      <div className="flex items-start gap-3 text-[#6E6E73] mb-4">
                        <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#FF3B30]" />
                        <div>
                          <p className="font-semibold text-[#0B1221]">{event.location}</p>
                          {event.address && (
                            <p className="text-sm mt-1">{event.address}</p>
                          )}
                        </div>
                      </div>
                      
                      <p className="text-[#6E6E73] leading-relaxed mb-6">
                        {getDescription(event)}
                      </p>

                      {/* Mini Map */}
                      <div className="mb-6 border border-[#E5E5EA] overflow-hidden">
                        <div className="aspect-[2/1] lg:aspect-[3/1]">
                          <iframe
                            data-testid={`event-map-${event.id}`}
                            src={`https://www.google.com/maps/embed/v1/place?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&q=${encodeURIComponent(event.address || event.location)}&zoom=13`}
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title={`Map - ${event.name}`}
                          />
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-3">
                        {event.url && (
                          <a
                            href={event.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`event-link-${event.id}`}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-[#0B1221] text-white hover:bg-[#1A2639] transition-colors duration-300 text-sm font-medium"
                          >
                            {t('events.visitWebsite')}
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        )}
                        <a
                          href={getGoogleMapsUrl(event)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-6 py-3 bg-[#F5F5F7] text-[#0B1221] hover:bg-[#E5E5EA] transition-colors duration-300 text-sm font-medium"
                        >
                          <Navigation className="w-4 h-4" />
                          {language === 'it' ? 'Indicazioni' : language === 'de' ? 'Wegbeschreibung' : language === 'fr' ? 'Itinéraire' : 'Directions'}
                        </a>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-[#F5F5F7]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <p className="text-[#6E6E73] text-lg max-w-2xl mx-auto">
            {language === 'it' && 'Venite a trovarci alle fiere nautiche più importanti d\'Europa. Saremo lieti di mostrarvi le ultime novità e rispondere alle vostre domande.'}
            {language === 'de' && 'Besuchen Sie uns auf den wichtigsten Bootsmessen Europas. Wir freuen uns, Ihnen die neuesten Modelle zu zeigen und Ihre Fragen zu beantworten.'}
            {language === 'fr' && 'Venez nous rendre visite aux salons nautiques les plus importants d\'Europe. Nous serons heureux de vous présenter les dernières nouveautés et de répondre à vos questions.'}
            {language === 'en' && 'Come visit us at Europe\'s most important boat shows. We will be happy to show you the latest models and answer your questions.'}
          </p>
        </div>
      </section>
    </div>
  );
};
